// packages/base/src/kb-index/flow-colors.ts
//
// Single source of truth for the flow-type → connector colour palette.
//
// Before this module the palette was DUPLICATED and divergent across two
// renderers: the web-ui React Flow edge (`EDGE_KIND_STYLES` in
// `ConnectionEdge.tsx`, keyed on the lowercase `ConnectionKind`) and the
// server SVG export (`edgeColorFor` in `auto-view-svg-renderer.ts`, keyed on
// the PascalCase `interfaceType`). They disagreed on every hue. This module
// collapses both onto ONE palette so that:
//
//   • the connector stroke and its same-colour flow-name label are provably
//     identical, and
//   • the React Flow canvas and the exported SVG agree.
//
// The palette adopted is the (richer) web-ui palette — it is the primary
// user-facing canvas and already covered `mechanical` + `reference`, which the
// server map lacked.
//
// NOTE: colour is intentionally NOT folded into the methodology
// `connectionTaxonomy` (kb-methodology/default.ts) — methodology is
// workspace-overridable and colour is a presentation concern that must stay
// stable across methodologies.
/**
 * The one palette. Hexes are the web-ui `EDGE_KIND_STYLES` strokes; keep this
 * the authoritative copy and derive both renderers from it.
 */
export const FLOW_COLORS = {
    energy: '#D97706', // amber-600 — power / electricity
    material: '#0891B2', // cyan-600 — fluid / material flow
    mechanical: '#7C2D12', // dark brown / rust — rigid mechanical / torque
    information: '#2563EB', // blue-600 — signals / data
    reference: '#737373', // neutral-500 — references / unknown fallback
};
/** Colour used for non-flow relations and unclassifiable interface types. */
export const DEFAULT_FLOW_COLOR = FLOW_COLORS.reference;
/**
 * Classify a SysML `interfaceType` string (e.g. `'EnergyFlow'`,
 * `'MechanicalConnection'`) into a {@link FlowColorKind}.
 *
 * Substring-based and order-sensitive: `mechanical` is tested before
 * `material` (a hypothetical `MechanicalMaterialFlow` should classify as
 * mechanical), and `energy` before the `reference` catch-all. Anything
 * unrecognised falls back to `'reference'`.
 *
 * The web-ui `deriveEdgeKind` (layout-builder.ts) intentionally keeps the
 * SAME keyword ladder but returns `undefined` (→ neutral default stroke) for
 * an unrecognised type instead of `'reference'`. Keep the two ladders in sync
 * by hand when adding a flow type; they are deliberately NOT auto-shared
 * because of that fallback difference.
 */
export function classifyFlowKind(interfaceType) {
    const lower = interfaceType.toLowerCase();
    if (lower.includes('mechanical'))
        return 'mechanical';
    if (lower.includes('energy') || lower.includes('electrical'))
        return 'energy';
    if (lower.includes('information') || lower.includes('signal') || lower.includes('data')) {
        return 'information';
    }
    if (lower.includes('material') || lower.includes('fluid'))
        return 'material';
    return 'reference';
}
/**
 * Connector colour for a SysML `interfaceType` string. Used by the server SVG
 * renderer (which keys natively on PascalCase `interfaceType`). The web-ui
 * indexes {@link FLOW_COLORS} directly by its already-derived `ConnectionKind`.
 */
export function flowColorForInterfaceType(interfaceType) {
    return FLOW_COLORS[classifyFlowKind(interfaceType)];
}
//# sourceMappingURL=flow-colors.js.map